import { useCartContext } from '../Contexts/CartContext';
import { useCheckoutContext } from '../Contexts/CheckoutContext';
import { useLayoutContext } from '../Contexts/LayoutContext';
import { useLoginContext } from '../Contexts/LoginContext';
import { useOrderTypeContext } from '../Contexts/OrderTypeContext';
import { useStoreContext } from '../Contexts/StoreContext';
import CartService, { RemoveAllProps } from '../Services/CartService';
import CustomerService from '../Services/CustomerService';
import OrderService from '../Services/OrderService';
import { CART_STATUS_FINISHED, ERROR_CART_NOT_FOUND } from '../Utils/Constants/Cart';
import { formatCartId } from '../Utils/Formatters';
import { useProductsHook } from './ProductsHook';
import { useStoreHook } from './StoreHook';
import { TokenManager } from '../Services/TokenManager';
import { useTranslation } from 'react-i18next';
import { ORDER_TYPE_DELIVERY_ID, ORDER_TYPE_STORE_COUNTER_ID } from '../Utils/Constants/Service';

export const useCartHook = () => {

  const { t } = useTranslation();
  const { setMainLoading, setMainLoadingIcon, setMainLoadingIconSearchCart, setMainLoadingIconError, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton, setMainLoadingIconNoSearchMap, setVersionFront } = useLayoutContext();
  const { cartDisplay, setCartDisplay, setShowCartIsFinished, setCartFinished, setCartConference, setShowCart, setShowCartConference, setShowConfirmTable, setCurrentCart, getFormattedItemsfromCartConference, setLoadingCurrentCart, setLoadingCoupon, currentCart, setLoadingUpdateQuantity, setLoadingUpdateQuantityProductId } = useCartContext();
  const { currentStore, table, setTable, setTables, setCashier, setRequiredTable, setCurrentAddress } = useStoreContext();
  const { orderType, setOrderType, getOrderTypeIdByName, isStoreCounter } = useOrderTypeContext();
  const { setGeolocation } = useLoginContext();
  const { setShowPixCode, order, setOrder } = useCheckoutContext();
  const { sendProductsStoreCounter } = useProductsHook();
  const { getActiveCart, updateCartTable, createGuestToken } = useStoreHook();

  const createCart = async () => {
    setMainLoading(true);
    setMainLoadingTitle(t('login.store_counter.opening_loading'));
    setMainLoadingMessage(t('layout.loading_main.default_message'));

    const orderTypeId = getOrderTypeIdByName(orderType);
    if (orderTypeId == null) {
      console.error('invalid orderType');
      setMainLoading(false);
      return;
    }

    if (!orderTypeId || !currentStore?.id) {
      // console.error('Dados inválidos para criar comanda');
      setMainLoading(false);
      return;
    }

    const payload = {
      store_id: currentStore.id,
      order_type: Number(orderTypeId),
      store_table_id: table,
    };

    try {
      const cartService = new CartService(orderType);
      const cartCreated = await cartService.create(payload);

      if (cartCreated) {

        //* forçando seleção de mesa
        if (!table && !cartCreated.cart.table?.id ) {
          setRequiredTable(true);
        }

        setCartDisplay({
          id: cartCreated.cart.id,
          table: {
            id: table || cartCreated.cart.table?.id,
            name: cartCreated.cart.table?.name || '',
          },
        });
        setTable(null);
        setCartFinished(null);
        setShowCartIsFinished(false);
        setOrder(null);
      }
    } catch (error: any) {
      if (error.response?.data?.error === 'could_not_refresh_token') {
        TokenManager.setToken(null);
      }

      setTimeout(() => {
        setMainLoadingIcon(false);
        setMainLoadingIconError(true);
        setMainLoadingCloseButton(true);
        setMainLoadingTitle(t('errors.default_title'));
        setMainLoadingMessage(t('login.store_counter.error_opening_comanda'));
        setMainLoading(true);
      }, 1250);

      // console.error('Erro ao criar comanda:', error);
    } finally {
      setMainLoading(false);
    }
  };


  interface CartFinishedProps {id: number, display_id: number | null, order: any}
  const cartFinished = ({id, display_id, order}: CartFinishedProps) => {
    setCartFinished({id, display_id, order});
    setShowCartIsFinished(true);
    setOrder(null);
    setCartConference([]);
    // setTable(cartDisplay?.table?.id); //* Mantendo a mesa do último cart
    setCartDisplay(null);
    setCashier(null);
  }

  const getOpenedCustomerCartDiffStored = async () => {
    try {

      const orderTypeId = getOrderTypeIdByName(orderType);
      if (orderTypeId == null) throw new Error('Tipo de pedido inválido');

      const customerService = new CustomerService(orderType);
      const payload = {
        store_id: currentStore?.id,
        order_type: Number(orderTypeId),
      };

      const getCart = await customerService.getCartOpen(payload);

      if (!getCart || !getCart.id) {
        setCartDisplay(null);
      } else if (!cartDisplay || cartDisplay.id !== getCart.id) {

        const displayCartId = getCart.display_id || getCart.id;

        setCartDisplay({
          id: getCart.id,
          display_id: getCart.display_id ? Number(getCart.display_id) : null,
          table: {
            id: cartDisplay?.table?.id || table,
            name: getCart.table?.name || '',
          },
        });

        setMainLoading(true);
        setMainLoadingTitle(t('login.store_counter.we_found_your_comanda', {number: formatCartId(displayCartId)}));
        setMainLoadingMessage(t('login.store_counter.checking_products_added_by_waiter'));
        setMainLoadingIcon(false);
        setMainLoadingIconSearchCart(true);
        setMainLoadingCloseButton(true);
        setTable(null);
        setCartFinished(null);
      }

      const hasRequiredTable = Boolean(getCart && Object.keys(getCart).length > 0 && getCart.id && !getCart.table);
      setRequiredTable(hasRequiredTable);

      return getCart;
    }
    catch (error) {
      throw error;
    }
    finally {}
  };

  const cartNotFound = () => {
    setMainLoadingIcon(false);
    setMainLoadingIconError(true);
    setMainLoading(true);
    setMainLoadingTitle(t('cart.comanda_is_closed_title', {number: formatCartId(cartDisplay.display_id || cartDisplay.id)}));
    setMainLoadingMessage(t('cart.comanda_is_closed_message'));
    setMainLoadingCloseButton(true);
    setTable(cartDisplay?.table?.id); //* Mantendo a mesa do último cart
    setCartDisplay(null);
    setShowCart(false);
    setShowCartConference(false);
    setCartConference([]);
    setCashier(null);
    setShowPixCode(false);
    setOrder(null);

    setRequiredTable(false);
    setShowConfirmTable(false);
  }

  const geolocationBlocked = () => {
    setMainLoadingIcon(false);
    setMainLoadingIconNoSearchMap(true);
    setMainLoadingTitle(t('layout.geolocation_permission.denied.title'));
    setMainLoadingMessage(t('layout.geolocation_permission.denied.message_to_open_comanda'));
    setMainLoadingCloseButton(true);
    setMainLoading(true);
  }

  const removeLogin = (showMessage: boolean = true) => {
    if (showMessage) {
      setMainLoadingIcon(false);
      setMainLoadingIconError(true);
      setMainLoading(true);
      setMainLoadingTitle(t('login.session_expired_title'));
      setMainLoadingMessage('');
      setMainLoadingCloseButton(true);
    }
    if (cartDisplay) {
      setTable(cartDisplay.table.id);
    }

    setCartDisplay(null);
    setCurrentCart(null);
    setOrder(null);
    setCurrentAddress(null);
    TokenManager.setToken(null);
    TokenManager.setGuestToken(null);

    //* Create guestToken and getActiveCart
    const orderTypeId = Number(getOrderTypeIdByName(orderType));
    if (orderTypeId !== ORDER_TYPE_STORE_COUNTER_ID && orderTypeId !== ORDER_TYPE_DELIVERY_ID) {
      createGuestToken(currentStore.id, orderTypeId)
        .then((resp: any) => {
            const payload = {
                store_id: currentStore.id, 
                order_type_id: orderTypeId, 
                order_type: orderType
            }
            getActiveCart(payload).catch(() => {});
        })
        .catch(() => {});
    }
  }

  const updatedFrontVersion = () => {
    setCartFinished(null);
    setCartDisplay(null);
    setCashier(null);
    setGeolocation(null);
    setTable(null);
    setTables(null);
    setVersionFront(process.env.REACT_APP_VERSION);

    TokenManager.setToken(null);
    TokenManager.setGuestToken(null);
    setCurrentCart(null);
    setOrder(null);
    setOrderType(null);
  }

  const cancelPayment = async (cart_id: number) => {

    setMainLoading(true);
    setMainLoadingTitle(t('checkout.cancel_payment_title'));
    setMainLoadingMessage(t('layout.loading_main.default_message'));

    const payload = {
      cart_id: cart_id,
    }
    try {
        const orderService = new OrderService(orderType);
        const orderCanceled = await orderService.cancelPayment(payload);
        if (orderCanceled.success) {
          setCartDisplay({...cartDisplay, order})
          setShowPixCode(false);
          setOrder(null);
          setMainLoading(false);
        }
        return orderCanceled;
    } catch (error) {
        // console.error('Error cancelPayment:', error);
        setMainLoadingIcon(false);
        setMainLoadingIconError(true);
        setMainLoadingTitle(t('errors.default_title'));
        setMainLoadingMessage(
          isStoreCounter
            ? t('errors.default_message_store_counter')
            : t('errors.default_message')
        );
        setMainLoadingCloseButton(true);
        throw error;
    }
  }

  const checkOrdersOpen = async () => {
    setMainLoading(true);
    setMainLoadingTitle(t('checkout.check_orders_open_title'));
    setMainLoadingMessage(t('layout.loading_main.default_message'));
    try {
        const orderService = new OrderService(orderType);
        const ordersOpen = await orderService.checkOrdersOpen();
        setMainLoading(false);
        return ordersOpen;
    } catch (error) {
        setMainLoadingIcon(false);
        setMainLoadingIconError(true);
        setMainLoadingTitle(t('errors.default_title'));
        setMainLoadingMessage(t('errors.default_message'));
        setMainLoadingCloseButton(true);
        // console.error('Error cancelPayment:', error);
    }
  }

  const applyCouponCode = async (couponCode: string) => {
    const payload = {
      coupon_code: couponCode,
      order_type: Number(getOrderTypeIdByName(orderType)),
      store_id: currentStore.id,
    }
    try {
        setLoadingCoupon(true);
        const cart_id = isStoreCounter ? cartDisplay.id : currentCart.cart_id;
        const cartService = new CartService(orderType);
        const couponCodeApplied = await cartService.applyCoupon(cart_id, payload);
        if (couponCodeApplied.success) {
          setCurrentCart(couponCodeApplied.cart);
        } else {
          setMainLoading(true);
          setMainLoadingIcon(false);
          setMainLoadingIconError(true);
          setMainLoadingTitle(couponCodeApplied.message);
          setMainLoadingMessage(t('checkout.coupon.check_code_entered_title'));
          setMainLoadingCloseButton(true);
        }
        setLoadingCoupon(false);
        return couponCodeApplied;
    } catch (error) {
        setLoadingCoupon(false);
        throw error;
    }
  }

  const removeCouponCode = async (couponCode: string) => {
    const payload = {
      coupon_code: couponCode,
      order_type: Number(getOrderTypeIdByName(orderType)),
      store_id: currentStore.id,
    }
    try {
        setLoadingCoupon(true);
        const cart_id = isStoreCounter ? cartDisplay.id : currentCart.cart_id;
        const cartService = new CartService(orderType);
        const couponCodeRemoved = await cartService.removeCoupon(cart_id, payload);
        if (couponCodeRemoved.success) {
          setCurrentCart(couponCodeRemoved.cart);
        }
        setLoadingCoupon(false);
        return couponCodeRemoved;
    } catch (error) {
        setLoadingCoupon(false);
        throw error;
    }
  }

  const changeQuantityProduct = async (id: number, quantity: number) => {
    const payload = {
      id: id,
      order_type: Number(getOrderTypeIdByName(orderType)),
      quantity: quantity,
    }
    try {
        setLoadingUpdateQuantity(true);
        setLoadingUpdateQuantityProductId(id);
        const cart_id = isStoreCounter ? cartDisplay.id : currentCart.cart_id;
        const cartService = new CartService(orderType);
        const response = await cartService.changeQuantity(cart_id, payload);
        if (response) {
          setCurrentCart(response);
        }
        setLoadingUpdateQuantity(false);
        setLoadingUpdateQuantityProductId(null);
    } catch (error) {
        setLoadingUpdateQuantity(false);
        setLoadingUpdateQuantityProductId(null);

        setMainLoading(true);
        setMainLoadingIcon(false);
        setMainLoadingIconError(true);
        setMainLoadingCloseButton(true);
        setMainLoadingTitle(t('errors.default_title'));
        setMainLoadingMessage(t('products.error_change_quantity_title'));
    }
  }

  const deleteProduct = async (id: number) => {
    const payload = {
      id: id,
      order_type: Number(getOrderTypeIdByName(orderType)),
    }
    try {
        setLoadingUpdateQuantity(true);
        setLoadingUpdateQuantityProductId(id);
        const cart_id = isStoreCounter ? cartDisplay.id : currentCart.cart_id;
        const cartService = new CartService(orderType);
        const response = await cartService.deleteProductFromCart(cart_id, payload);
        if (response) {
          setCurrentCart(response);
        }
        setLoadingUpdateQuantity(false);
        setLoadingUpdateQuantityProductId(null);
    } catch (error) {
      setLoadingUpdateQuantity(false);
      setLoadingUpdateQuantityProductId(null);

      setMainLoading(true);
      setMainLoadingIcon(false);
      setMainLoadingIconError(true);
      setMainLoadingCloseButton(true);
      setMainLoadingTitle(t('errors.default_title'));
      setMainLoadingMessage(t('products.error_remove_product_title'));
    }
  }

  const removeOldCart = () => {
    setCartFinished(null);
    setCartDisplay(null);
    setCashier(null);
    setGeolocation(null);
    setTable(null);
    TokenManager.setToken(null);
  }

  const handleSend = () => {
    setShowConfirmTable(false);
    const showCart = async () => {
      const payload = {
          cart_id: cartDisplay.id,
          store_id: currentStore.id,
          order_type: Number(getOrderTypeIdByName(orderType)),
      }
      try {
          setMainLoading(true);
          setMainLoadingIcon(true);
          setMainLoadingTitle(t('login.store_counter.checking_your_comanda'));
          setMainLoadingMessage(t('layout.loading_main.default_message'));
          const cartService = new CartService(orderType);
          const getCart = await cartService.show(payload);

          if (getCart) {
              if (getCart.status === CART_STATUS_FINISHED) {
                  cartFinished({
                    id: cartDisplay.id,
                    display_id: cartDisplay.display_id ? Number(cartDisplay.display_id) : null,
                    order: getCart.order
                  });
                  setMainLoading(false);
                  setShowCartConference(false);
              }
              else {
                  const payload = {
                      apply_tax: true,
                      table_id: cartDisplay.table.id,
                      table_name: cartDisplay.table.name,
                      store_id: currentStore.id,
                      order_type: Number(getOrderTypeIdByName(orderType)),
                      items: getFormattedItemsfromCartConference(cartDisplay.id)
                  }

                  sendProductsStoreCounter(cartDisplay.id, payload).then((success: any) => {
                      if (success) {
                          setShowCartConference(false);
                      }
                  });
              }
          } else {
              setMainLoading(false);
          }
        } catch (error: any) {
            if (error.response?.data?.error === ERROR_CART_NOT_FOUND) {
                cartNotFound();
            } else {
                setMainLoading(false);
            }
        }
    }
    showCart();
  }

  const removeAll = async (cart_id: number, payload: RemoveAllProps) => {
    try {
      setLoadingCurrentCart(true);
      const cartService = new CartService(orderType);
      return await cartService.removeAll(cart_id, payload)
        .then(response => {
          const orderTypeId = Number(getOrderTypeIdByName(orderType));
          const payload = {
            store_id: currentStore.id,
            order_type_id: orderTypeId,
            order_type: orderType
          }
          getActiveCart(payload).catch(() => {});
          setShowCart(false);
        }).finally(() => {
          setLoadingCurrentCart(true);
          setMainLoading(false);
        });
      } catch (error) {
        setLoadingCurrentCart(false);

        setMainLoading(true);
        setMainLoadingIcon(false);
        setMainLoadingIconError(true);
        setMainLoadingCloseButton(true);
        setMainLoadingTitle(t('errors.default_title'));
        setMainLoadingMessage(t('products.error_remove_all_products_title'));
      }
  };

  const checkIsWhatsappAvailable = async (): Promise<boolean> => {
    try {
      const customerService = new CustomerService(orderType);
      const isWhatsappAvailable = await customerService.checkWhatsapp();
      return isWhatsappAvailable.status === 'success';
    } catch (error) {
      return false;
    }
  };

  const getCustomerCart = async () => {
    try {
      const customerService = new CustomerService(orderType);
      const payload = {
        store_id: currentStore.id,
        order_type: Number(getOrderTypeIdByName(orderType)),
      }
      const getCart = await customerService.getCartOpen(payload).catch(() => {});

      if (getCart && Object.keys(getCart).length > 0 && getCart.id) {
        setCartDisplay({
          id: getCart.id,
          table: {
            id: getCart.table?.id,
            name: getCart.table?.name,
          }
        });

        setMainLoading(true);
        setMainLoadingTitle(t('login.store_counter.we_found_your_comanda', {number: formatCartId(getCart.id)}));
        setMainLoadingMessage(t('login.store_counter.checking_products_added_by_waiter'));
        setMainLoadingIcon(false);
        setMainLoadingIconSearchCart(true);
        setMainLoadingCloseButton(true);
        setCartFinished(null);
        setTable(null);
      } else {
        createCart();
      }
    } catch (error) {
        setMainLoading(true);
        setMainLoadingIcon(false);
        setMainLoadingIconError(true);
        setMainLoadingCloseButton(true);
        setMainLoadingTitle(t('errors.default_title'));
        setMainLoadingMessage(t('errors.default_message'));
    }
  }

  const handleUpdateCartTable = (tableId: number | null) => {
    const cart_id = currentCart ? currentCart.cart_id : null;
    if (cart_id) {
        setMainLoading(true);
        setMainLoadingMessage(t('layout.loading_main.default_message'));
        updateCartTable(cart_id, tableId)
            .then((resp) => {
                if (resp.cart) {
                    setCurrentCart(resp.cart);
                    setMainLoading(false);
                }
            })
            .catch((error: any) => {
                setMainLoadingIcon(false);
                setMainLoadingIconError(true);
                setMainLoadingTitle(t('errors.default_title'));
                setMainLoadingMessage(t('errors.default_message'));
                setMainLoadingCloseButton(true)
            })
            .finally(() => {
                setShowConfirmTable(false);
            });
    } else {
        setShowConfirmTable(false);
    }
  }

  const handleUpdateCartTableStoreCounter = (table: any) => {
    setShowConfirmTable(false);
    const cart_id = cartDisplay ? cartDisplay.id : null;
    if (cart_id) {
        updateCartTable(cart_id, table.id).catch((error: any) => {
            if (error.status === 404) {
                cartNotFound();
                setRequiredTable(false);
            }
        });
    }
  }

  return {
    createCart,
    cartFinished,
    getOpenedCustomerCartDiffStored,
    cartNotFound,
    cancelPayment,
    removeLogin,
    geolocationBlocked,
    removeOldCart,
    updatedFrontVersion,
    handleSend,
    removeAll,
    applyCouponCode,
    removeCouponCode,
    changeQuantityProduct,
    deleteProduct,
    checkOrdersOpen,
    checkIsWhatsappAvailable,
    getCustomerCart,
    handleUpdateCartTable,
    handleUpdateCartTableStoreCounter
  };
}
